import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { PriceLevel } from 'depoto-core/src/entities'
import { NotificationService } from '../../services'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { FormControl, FormGroup } from '@angular/forms'
import { historyBack } from '../../utils'
import { TranslateService } from '@ngx-translate/core'
import { TitleService } from '../../services'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { PaymentService } from '../../services/entity-services/payment.service'
import { PriceLevelService } from '../../services/entity-services/priceLevel.service'
import { updateCachedListQueries } from '../../services/query-cache.service'

const priceLevelSchema = {
  id: null,
  name: null,
  isPercentage: null,
  percent: null,
  withVat: null,
  currency: {
    id: null,
    name: null,
    ratio: null,
  },
  externalId: null,
  company: {
    id: null,
    name: null,
  },
}

@Component({
  selector: 'app-price-level',
  templateUrl: './price-level.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PriceLevelComponent implements CheckDataChanges {
  private priceLevelService = inject(PriceLevelService)
  private paymentService = inject(PaymentService)
  private queryClient = injectQueryClient()

  private initialFormValue = signal<any>(null)
  submitted = signal(false)
  // public currencyOptions: Currency[] = []
  priceLevelForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, []),
    isPercentage: new FormControl({ value: false, disabled: false }, []),
    percent: new FormControl({ value: 0, disabled: false }, []),
    withVat: new FormControl({ value: false, disabled: false }, []),
    currency: new FormControl({ value: '', disabled: false }, []),
  })

  readonly queryCurrenciesOptions = injectQuery(() => ({
    queryKey: ['currenciesOptions'],
    queryFn: async () => {
      return await this.paymentService.getCurrencies({}, { id: null, name: null, ratio: null }).then(result => {
        return result.items
      })
    },
  }))

  readonly queryPriceLevel = injectQuery(() => ({
    queryKey: ['priceLevel', Number(this.route.snapshot.params['id'])],
    queryFn: async () => {
      const id = this.route.snapshot.params['id']
      if (id) {
        return await this.priceLevelService.getPriceLevel(id, priceLevelSchema)
      } else {
        return new PriceLevel()
      }
    },
    onSuccess: data => {
      this.titleService.updateTitleWithObject(data['id'], data['name'], 'title.price-level-id')
      this.fillFormValue(data)
    },
  }))

  createPriceLevel = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      if (this.priceLevelForm.invalid) {
        throw new Error('Form is invalid')
        return
      }

      const n = this.getDataToSave()

      // Return the created tag so onSuccess receives the created entity (with id)
      return await this.priceLevelService.createPriceLevel(n, priceLevelSchema)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.priceLevelForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.priceLevelForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('ALERT PRICE-LEVEL CREATED'))
      // Update detail cache for this price level
      this.queryClient.setQueryData(['priceLevel', Number(data.id)], data)
      // Update all cached list queries beginning with ['price-levels', ...] to reflect change immediately
      updateCachedListQueries(this.queryClient, ['price-levels'], data, { mode: 'insert', insertPosition: 'start' })

      this.removePriceLevelListing()

      this.router.navigate(['/price-level', data['id']], { replaceUrl: true })
    },
  }))

  updatePriceLevel = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      const n = this.getDataToSave()

      // Return the updated entity so `onSuccess(data)` receives the server response
      return await this.priceLevelService.updatePriceLevel(
        { id: Number(this.router.url.split('/')[2]), ...n },
        priceLevelSchema
      )
    },
    onSuccess: data => {
      this.submitted.set(false)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.priceLevelForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.priceLevelForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('ALERT PRICE-LEVEL UPDATED'))
      // Update detail cache for this price level
      this.queryClient.setQueryData(['priceLevel', Number(data['id'])], data)
      // Update all cached list queries beginning with ['price-levels', ...] so listings reflect the update immediately
      updateCachedListQueries(this.queryClient, ['price-levels'], data)

      this.removePriceLevelsListings()

      this.router.navigate(['/price-level', data['id']])
    },
    enabled: !!this.router.url.split('/')[2],
  }))

  deletePriceLevel = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      const confirmation = confirm(
        this.translateService.instant('vat.delete-confirm', { name: this.priceLevelForm.value.name })
      )
      if (confirmation) {
        await this.priceLevelService.deletePriceLevel(this.queryPriceLevel.data())
      } else {
        // todo
      }
    },
    onSuccess: () => {
      this.submitted.set(false)
      this.notificationService.success(this.translateService.instant('ALERT PRICE-LEVEL DELETED'))
      // Prune deleted price level from any cached lists (supports infinite queries and arrays)
      const idStr = String(this.route.snapshot.params['id'])
      updateCachedListQueries(this.queryClient, ['price-levels'], { id: Number(idStr) } as any, { mode: 'delete' })
      // Remove detail cache for this price level

      this.removePriceLevelsListings()

      this.queryClient.removeQueries({ queryKey: ['priceLevel', Number(idStr)], exact: true })
      this.router.navigate(['/price-levels'], { replaceUrl: true })
    },
  }))

  isCreating = computed(() => {
    return !this.route.snapshot.params['id']
  })
  loading = computed(() => {
    return this.queryPriceLevel.isFetching() || this.queryPriceLevel.isPending()
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private titleService: TitleService
  ) {
    // Populate the form whenever query data becomes available/changes (works for cache hits too)
    effect(() => {
      const d = this.queryPriceLevel.data()
      const id = this.route.snapshot.params['id']
      if (id && d && this.priceLevelForm.pristine) {
        this.fillFormValue(d)
      }
    })
  }

  public fillFormValue(priceLevel) {
    const value = {
      name: priceLevel.name ?? '',
      isPercentage: priceLevel.isPercentage ?? false,
      percent: priceLevel.percent ?? 0,
      withVat: priceLevel.withVat ?? false,
      currency: priceLevel.currency?.id ?? '',
    }

    this.priceLevelForm.setValue(value)
    // Store initial values snapshot and reset form dirty state
    this.initialFormValue.set(structuredClone(value))
    this.priceLevelForm.markAsPristine()
  }

  getDirtyValues(form = this.priceLevelForm) {
    const dirtyValues = {}

    Object.keys(form.controls).forEach(key => {
      const currentControl = form.controls[key]

      if (currentControl.dirty) {
        if (currentControl.controls) dirtyValues[key] = this.getDirtyValues(currentControl)
        else dirtyValues[key] = currentControl.value
      }
    })

    return dirtyValues
  }

  getDataToSave() {
    const current = this.priceLevelForm.getRawValue()
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    const routeId = this.route.snapshot.params['id']
    if (routeId) patch.id = Number(routeId)

    // Always include required fields
    patch.name = current.name

    // Optional fields – include only when changed against initial snapshot
    const norm = (v: any) => (v === undefined || v === null ? '' : v)
    const idArray = (arr: any[]) =>
      Array.isArray(arr) ? arr.map((x: any) => x?.id).filter((v: any) => v !== undefined && v !== null) : []
    const eqArrayIds = (a: any[], b: any[]) => {
      const aa = idArray(a)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      const bb = idArray(b)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }

    if (current.currency !== initial?.currency) {
      patch.currency = this.queryCurrenciesOptions.data().find(c => String(c.id) === String(current.currency))
    }
    if (norm(current.isPercentage) !== norm(initial?.isPercentage)) {
      patch.isPercentage = current.isPercentage
    }
    if (norm(current.percent) !== norm(initial?.percent)) {
      patch.percent = current.percent
    }
    if (norm(current.withVat) !== norm(initial?.withVat)) {
      patch.withVat = current.withVat
    }

    return patch
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.priceLevelForm.getRawValue() as any
    const initial = this.initialFormValue() as any

    // Fallback: if initial snapshot not ready yet
    if (!initial) {
      return this.priceLevelForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))

    return (
      norm(current?.name) !== norm(initial?.name) ||
      norm(current?.isPercentage ?? '') !== norm(initial?.isPercentage ?? '') ||
      norm(current?.percent ?? false) !== norm(initial?.percent ?? false) ||
      norm(current?.withVat ?? false) !== norm(initial?.withVat ?? false) ||
      norm(current?.currency ?? false) !== norm(initial?.currency ?? false)
    )
  }

  removePriceLevelListing() {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['priceLevels'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  private removePriceLevelsListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['priceLevels'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  goBack(): void {
    historyBack()
  }
}
